import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import KeyboardArrowDownIcon from "@mui/icons-material/KeyboardArrowDown";
import SearchIcon from "@mui/icons-material/Search";
import RefreshIcon from "@mui/icons-material/Refresh";
import {
  Alert,
  Autocomplete,
  Box,
  Button,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  InputAdornment,
  MenuItem,
  Stack,
  TextField,
  Typography,
} from "@mui/material";

import { getAllEmpleados } from "../../../../rrhh/empleados/api/empleadosApi";
import { createStockCount, getStockCountCreatePreview } from "../api/stockCountsApi";
import { getStockCountErrorMessage } from "../utils/stockCountError";

const EMPLOYEE_PAGE_SIZE = 20;
const SEARCH_DEBOUNCE_MS = 300;
const STOCK_COUNT_TIME_ZONE = "America/Argentina/Buenos_Aires";
const SAME_EMPLOYEE_ERROR =
  "El contador y el auditor deben ser empleados distintos.";
const SCOPE_OPTIONS = [
  { value: "KITCHEN", label: "Cocina" },
  { value: "BAR", label: "Barra" },
  { value: "BOTH", label: "Ambas" },
];
const SECTOR_LABELS = { KITCHEN: "Cocina", BAR: "Barra" };

function emptyAssignments() {
  return {
    KITCHEN: { counter: null, auditor: null },
    BAR: { counter: null, auditor: null },
  };
}

const fieldSx = {
  "& .MuiInputLabel-root": {
    bgcolor: "common.white",
    px: 0.5,
  },
  "& .MuiOutlinedInput-root": {
    minHeight: 44,
    borderRadius: "14px",
    bgcolor: "common.white",
    fontSize: 14,
    fontWeight: 650,
    "& fieldset": { borderColor: "neutral.150" },
    "&:hover fieldset": { borderColor: "neutral.300" },
    "&.Mui-focused fieldset": { borderColor: "primary.500" },
  },
  "& .MuiInputBase-input::placeholder": {
    color: "neutral.600",
    opacity: 0.9,
    fontWeight: 600,
  },
};

function unwrapHttp(response) {
  if (
    response &&
    typeof response === "object" &&
    "data" in response &&
    !Array.isArray(response.data) &&
    !("meta" in response)
  ) {
    return response.data;
  }
  return response;
}

function todayArgentinaYmd() {
  return new Intl.DateTimeFormat("en-CA", {
    timeZone: STOCK_COUNT_TIME_ZONE,
    year: "numeric",
    month: "2-digit",
    day: "2-digit",
  }).format(new Date());
}

function normalizeEmployee(employee) {
  if (!employee?.id) return null;
  return {
    id: Number(employee.id),
    fullName: String(employee.fullName || employee.nombre || "").trim(),
    dni: employee.dni ? String(employee.dni) : "",
    status: employee.status || "",
  };
}

function getEmployeeLabel(employee) {
  if (!employee) return "";
  return employee.fullName || "—";
}

function EmployeeAutocomplete({
  label,
  placeholder,
  value,
  onChange,
  disabled = false,
  error = "",
  excludedEmployeeId = null,
}) {
  const requestIdRef = useRef(0);
  const [inputValue, setInputValue] = useState("");
  const [search, setSearch] = useState("");
  const [page, setPage] = useState(1);
  const [options, setOptions] = useState([]);
  const [totalPages, setTotalPages] = useState(0);
  const [loading, setLoading] = useState(false);
  const [loadError, setLoadError] = useState("");

  useEffect(() => {
    setInputValue(value ? getEmployeeLabel(value) : "");
    setSearch("");
    setPage(1);
    setLoadError("");
  }, [value]);

  useEffect(() => {
    if (value && inputValue === getEmployeeLabel(value)) {
      if (search) setSearch("");
      return undefined;
    }
    const timeout = setTimeout(() => {
      const normalized = inputValue.trim();
      if (normalized !== search) {
        setOptions([]);
        setPage(1);
        setSearch(normalized);
      }
    }, SEARCH_DEBOUNCE_MS);
    return () => clearTimeout(timeout);
  }, [inputValue, search, value]);

  const fetchEmployees = useCallback(
    async (signal, isMounted = () => true) => {
      const requestId = ++requestIdRef.current;
      setLoading(true);
      setLoadError("");
      try {
        const response = await getAllEmpleados({
          page,
          limit: EMPLOYEE_PAGE_SIZE,
          search: search || undefined,
          status: "ACTIVE",
          signal,
        });
        if (signal.aborted || !isMounted() || requestId !== requestIdRef.current) return;

        const payload = unwrapHttp(response);
        const list = Array.isArray(payload?.data)
          ? payload.data
          : Array.isArray(payload?.items)
            ? payload.items
            : Array.isArray(payload)
              ? payload
              : [];
        const normalized = list
          .map(normalizeEmployee)
          .filter(Boolean);

        setOptions((current) => {
          if (page === 1) return normalized;
          const byId = new Map(current.map((option) => [option.id, option]));
          for (const option of normalized) byId.set(option.id, option);
          return [...byId.values()];
        });
        setTotalPages(
          Number(payload?.meta?.totalPages) || (normalized.length ? 1 : 0)
        );
      } catch (requestError) {
        if (signal.aborted || !isMounted() || requestId !== requestIdRef.current) return;
        setOptions([]);
        setTotalPages(0);
        setLoadError(
          getStockCountErrorMessage(
            requestError,
            "No se pudieron cargar los empleados."
          )
        );
      } finally {
        if (!signal.aborted && isMounted() && requestId === requestIdRef.current) {
          setLoading(false);
        }
      }
    },
    [page, search]
  );

  useEffect(() => {
    if (disabled) return undefined;
    let mounted = true;
    const controller = new AbortController();
    fetchEmployees(controller.signal, () => mounted);
    return () => {
      mounted = false;
      controller.abort();
    };
  }, [disabled, fetchEmployees]);

  const helperText = error || loadError || " ";

  return (
    <Autocomplete
      fullWidth
      value={value}
      inputValue={inputValue}
      options={options}
      loading={loading}
      disabled={disabled}
      filterOptions={(employeeOptions) => employeeOptions}
      isOptionEqualToValue={(option, selected) =>
        Number(option.id) === Number(selected.id)
      }
      getOptionDisabled={(option) =>
        Boolean(
          excludedEmployeeId &&
            option?.id &&
            Number(option.id) === Number(excludedEmployeeId)
        )
      }
      getOptionLabel={getEmployeeLabel}
      popupIcon={<KeyboardArrowDownIcon sx={{ fontSize: 18 }} />}
      onInputChange={(_, nextInput, reason) => {
        if (reason === "input" || reason === "clear") setInputValue(nextInput);
      }}
      onChange={(_, option) => {
        onChange(option);
        setInputValue(option ? getEmployeeLabel(option) : "");
      }}
      ListboxProps={{
        "aria-label": `Resultados de ${label}`,
        "aria-labelledby": undefined,
        onScroll: (event) => {
          const listbox = event.currentTarget;
          const nearBottom =
            listbox.scrollTop + listbox.clientHeight >=
            listbox.scrollHeight - 24;
          if (nearBottom && !loading && page < totalPages) {
            setPage((current) => current + 1);
          }
        },
      }}
      noOptionsText={search ? "Sin resultados" : "No hay empleados activos"}
      loadingText="Buscando empleados..."
      renderOption={(props, option) => (
        <Box component="li" {...props} key={option.id}>
          <Stack spacing={0.1} sx={{ minWidth: 0 }}>
            <Typography sx={{ fontSize: 14, fontWeight: 700, color: "primary.900" }}>
              {option.fullName}
            </Typography>
            {option.dni ? (
              <Typography sx={{ fontSize: 12, color: "neutral.700", fontWeight: 600 }}>
                DNI {option.dni}
              </Typography>
            ) : null}
          </Stack>
        </Box>
      )}
      renderInput={(params) => (
        <TextField
          {...params}
          label={label}
          placeholder={placeholder}
          error={Boolean(error || loadError)}
          helperText={helperText}
          FormHelperTextProps={{
            role: error || loadError ? "alert" : undefined,
            sx: {
              mx: 0.4,
              mt: 0.45,
              minHeight: 18,
              fontSize: 11.5,
              fontWeight: 600,
            },
          }}
          InputProps={{
            ...params.InputProps,
            startAdornment: (
              <InputAdornment position="start">
                <SearchIcon sx={{ color: "primary.900", fontSize: 18, opacity: 0.9 }} />
              </InputAdornment>
            ),
            endAdornment: (
              <>
                {loading ? <CircularProgress size={18} /> : null}
                {params.InputProps.endAdornment}
              </>
            ),
          }}
          inputProps={{
            ...params.inputProps,
            "aria-label": label,
          }}
          sx={fieldSx}
        />
      )}
    />
  );
}

export default function StockCountRequestModal({ open, onClose, onCreated }) {
  const submittingRef = useRef(false);
  const [scope, setScope] = useState("KITCHEN");
  const [assignments, setAssignments] = useState(emptyAssignments);
  const [sharedItemsSector, setSharedItemsSector] = useState("KITCHEN");
  const [deadlineDate, setDeadlineDate] = useState("");
  const [fieldErrors, setFieldErrors] = useState({});
  const [submitError, setSubmitError] = useState("");
  const [saving, setSaving] = useState(false);
  const [preview, setPreview] = useState(null);
  const [previewLoading, setPreviewLoading] = useState(false);
  const [previewError, setPreviewError] = useState("");
  const [previewVersion, setPreviewVersion] = useState(0);

  const minDate = todayArgentinaYmd();

  useEffect(() => {
    if (!open) return;
    setScope("KITCHEN");
    setAssignments(emptyAssignments());
    setSharedItemsSector("KITCHEN");
    setDeadlineDate("");
    setFieldErrors({});
    setSubmitError("");
    setSaving(false);
    setPreview(null);
    setPreviewError("");
    setPreviewVersion(0);
    submittingRef.current = false;
  }, [open]);

  const activeSectors = useMemo(
    () => (scope === "BOTH" ? ["KITCHEN", "BAR"] : [scope]),
    [scope]
  );

  useEffect(() => {
    if (!open) return undefined;
    const controller = new AbortController();
    setPreviewLoading(true);
    setPreviewError("");
    getStockCountCreatePreview({
      scope,
      sharedItemsSector: scope === "BOTH" ? sharedItemsSector : undefined,
      signal: controller.signal,
    })
      .then((result) => {
        if (!controller.signal.aborted) setPreview(unwrapHttp(result));
      })
      .catch((error) => {
        if (controller.signal.aborted || error?.name === "AbortError") return;
        setPreview(null);
        setPreviewError(
          getStockCountErrorMessage(
            error,
            "No se pudo calcular el resumen del conteo."
          )
        );
      })
      .finally(() => {
        if (!controller.signal.aborted) setPreviewLoading(false);
      });
    return () => controller.abort();
  }, [open, scope, sharedItemsSector, previewVersion]);

  const updateAssignment = useCallback((sector, role, employee) => {
    setAssignments((current) => ({
      ...current,
      [sector]: { ...current[sector], [role]: employee },
    }));
    setFieldErrors((current) => ({
      ...current,
      [`${sector}.${role}`]: "",
    }));
    setSubmitError("");
  }, []);

  const validation = useMemo(() => {
    const errors = {};
    for (const sector of activeSectors) {
      const assignment = assignments[sector];
      if (!assignment.counter?.id) {
        errors[`${sector}.counter`] = "Seleccioná el empleado para conteo.";
      }
      if (!assignment.auditor?.id) {
        errors[`${sector}.auditor`] = "Seleccioná el empleado para auditoría.";
      }
      if (
        assignment.counter?.id &&
        assignment.auditor?.id &&
        Number(assignment.counter.id) === Number(assignment.auditor.id)
      ) {
        errors[`${sector}.auditor`] = SAME_EMPLOYEE_ERROR;
      }
    }
    if (!deadlineDate) {
      errors.deadlineDate = "Seleccioná la fecha de carga.";
    } else if (deadlineDate < minDate) {
      errors.deadlineDate = "La fecha de carga no puede estar vencida.";
    }
    return errors;
  }, [activeSectors, assignments, deadlineDate, minDate]);

  const canSubmit =
    !saving &&
    !previewLoading &&
    !previewError &&
    Boolean(preview?.catalogRevision) &&
    Number(preview?.totalLines) > 0 &&
    Boolean(deadlineDate) &&
    deadlineDate >= minDate &&
    activeSectors.every((sector) => {
      const { counter, auditor } = assignments[sector];
      return (
        counter?.id &&
        auditor?.id &&
        Number(counter.id) !== Number(auditor.id)
      );
    });

  function handleClose() {
    if (!submittingRef.current) onClose?.();
  }

  async function handleSubmit(event) {
    event.preventDefault();
    if (submittingRef.current || !canSubmit) return;

    setFieldErrors(validation);
    setSubmitError("");
    if (Object.keys(validation).length > 0) return;

    submittingRef.current = true;
    setSaving(true);

    try {
      await createStockCount({
        scope,
        lists: activeSectors.map((sector) => ({
          sector,
          counterEmployeeId: Number(assignments[sector].counter.id),
          auditorEmployeeId: Number(assignments[sector].auditor.id),
        })),
        ...(scope === "BOTH" ? { sharedItemsSector } : {}),
        catalogRevision: preview.catalogRevision,
        deadlineDate,
      });
      onCreated?.({
        successMessage: "La solicitud de conteo se creó correctamente.",
      });
      onClose?.();
    } catch (error) {
      const status = Number(error?.status || error?.response?.status || 0);
      const code = error?.data?.code || error?.response?.data?.code;
      if (status === 409 && code === "STOCK_COUNT_CATALOG_CHANGED") {
        setPreview(null);
        setPreviewError(
          error?.data?.message ||
            error?.response?.data?.message ||
            "Los productos del conteo cambiaron. Recalculá el resumen antes de continuar."
        );
      } else if (status === 409) {
        setSubmitError(
          "Ya existe una solicitud de conteo activa para la empresa. Cancelala o finalizala antes de crear una nueva."
        );
      } else {
        setSubmitError(
          getStockCountErrorMessage(
            error,
            "No se pudo crear la solicitud de conteo."
          )
        );
      }
    } finally {
      submittingRef.current = false;
      setSaving(false);
    }
  }

  return (
    <Dialog
      open={Boolean(open)}
      onClose={saving ? undefined : handleClose}
      fullWidth
      maxWidth="md"
      PaperProps={{
        sx: {
          borderRadius: "24px",
          bgcolor: "neutral.200",
          maxWidth: 820,
          maxHeight: "calc(100dvh - 48px)",
          overflow: "hidden",
        },
      }}
    >
      <Box
        component="form"
        onSubmit={handleSubmit}
        noValidate
        sx={{ display: "flex", flexDirection: "column", minHeight: 0, maxHeight: "calc(100dvh - 48px)" }}
      >
        <DialogTitle sx={{ px: 3, pt: 2.75, pb: 0.5, flexShrink: 0 }}>
          <Typography sx={{ fontWeight: 800, color: "primary.900", fontSize: 20 }}>
            Nueva solicitud de conteo
          </Typography>
          <Typography
            sx={{ mt: 0.75, color: "neutral.700", fontSize: 14, fontWeight: 650 }}
          >
            Completá los datos para asignar y programar un conteo.
          </Typography>
        </DialogTitle>

        <DialogContent sx={{ px: 3, pt: 2.25, pb: 1, minHeight: 0, overflowY: "auto" }}>
          <Box
            sx={{
              borderRadius: "20px",
              bgcolor: "common.white",
              px: 2,
              py: 3,
            }}
          >
            <Stack spacing={1.15}>
              <TextField
                select
                label="Alcance del conteo"
                value={scope}
                onChange={(event) => {
                  setScope(event.target.value);
                  setFieldErrors({});
                  setSubmitError("");
                }}
                disabled={saving}
                inputProps={{ "aria-label": "Alcance del conteo" }}
                sx={fieldSx}
              >
                {SCOPE_OPTIONS.map((option) => (
                  <MenuItem key={option.value} value={option.value}>
                    {option.label}
                  </MenuItem>
                ))}
              </TextField>

              {activeSectors.map((sector) => {
                const assignment = assignments[sector];
                const suffix = scope === "BOTH" ? ` · ${SECTOR_LABELS[sector]}` : "";
                const sameEmployee = Boolean(
                  assignment.counter?.id &&
                    assignment.auditor?.id &&
                    Number(assignment.counter.id) === Number(assignment.auditor.id)
                );
                return (
                  <Box key={sector}>
                    {scope === "BOTH" ? (
                      <Typography sx={{ mb: 1, fontSize: 14, fontWeight: 800, color: "primary.900" }}>
                        Lista {SECTOR_LABELS[sector]}
                      </Typography>
                    ) : null}
                    <Stack spacing={1.15}>
                      <EmployeeAutocomplete
                        label={`Empleado para conteo${suffix}`}
                        placeholder="Buscar empleado para conteo..."
                        value={assignment.counter}
                        onChange={(employee) => updateAssignment(sector, "counter", employee)}
                        disabled={saving}
                        error={fieldErrors[`${sector}.counter`] || ""}
                        excludedEmployeeId={assignment.auditor?.id}
                      />
                      <EmployeeAutocomplete
                        label={`Empleado para auditoría${suffix}`}
                        placeholder="Buscar empleado para auditoría..."
                        value={assignment.auditor}
                        onChange={(employee) => updateAssignment(sector, "auditor", employee)}
                        disabled={saving}
                        error={
                          sameEmployee
                            ? SAME_EMPLOYEE_ERROR
                            : fieldErrors[`${sector}.auditor`] || ""
                        }
                        excludedEmployeeId={assignment.counter?.id}
                      />
                    </Stack>
                  </Box>
                );
              })}

              {scope === "BOTH" ? (
                <TextField
                  select
                  label="Insumos compartidos"
                  value={sharedItemsSector}
                  onChange={(event) => setSharedItemsSector(event.target.value)}
                  disabled={saving}
                  helperText="Elegí en qué lista contar los insumos que pertenecen a Cocina y Barra."
                  inputProps={{ "aria-label": "Insumos compartidos" }}
                  sx={fieldSx}
                >
                  <MenuItem value="KITCHEN">Contarlos en Cocina</MenuItem>
                  <MenuItem value="BAR">Contarlos en Barra</MenuItem>
                </TextField>
              ) : null}

              <Box
                aria-label="Resumen del conteo"
                sx={{ borderRadius: "16px", bgcolor: "neutral.100", p: 1.5 }}
              >
                <Stack spacing={1.25}>
                  <Stack direction="row" alignItems="center" justifyContent="space-between">
                    <Typography sx={{ fontSize: 14, fontWeight: 850, color: "primary.900" }}>
                      Resumen de listas
                    </Typography>
                    {previewLoading ? <CircularProgress size={18} /> : null}
                  </Stack>

                  {previewError ? (
                    <Alert
                      severity="warning"
                      action={
                        <Button
                          color="inherit"
                          size="small"
                          startIcon={<RefreshIcon />}
                          onClick={() => setPreviewVersion((current) => current + 1)}
                        >
                          Recalcular
                        </Button>
                      }
                      sx={{ borderRadius: "12px", fontWeight: 650 }}
                    >
                      {previewError}
                    </Alert>
                  ) : null}

                  {!previewLoading && !previewError && preview ? (
                    <>
                      <Stack direction={{ xs: "column", sm: "row" }} spacing={1}>
                        {preview.lists?.map((list) => {
                          const assignment = assignments[list.sector];
                          return (
                            <Box key={list.sector} sx={{ flex: 1, borderRadius: "14px", bgcolor: "common.white", p: 1.25 }}>
                              <Typography sx={{ fontWeight: 850, color: "primary.900", fontSize: 13.5 }}>
                                Lista {SECTOR_LABELS[list.sector]}
                              </Typography>
                              <Typography sx={{ mt: 0.35, color: "neutral.700", fontSize: 12.5, fontWeight: 700 }}>
                                {list.linesCount} {list.linesCount === 1 ? "línea" : "líneas"}
                              </Typography>
                              <Typography sx={{ mt: 0.75, color: "neutral.700", fontSize: 12 }}>
                                Contador: {assignment?.counter?.fullName || "Sin asignar"}
                              </Typography>
                              <Typography sx={{ color: "neutral.700", fontSize: 12 }}>
                                Auditor: {assignment?.auditor?.fullName || "Sin asignar"}
                              </Typography>
                            </Box>
                          );
                        })}
                      </Stack>

                      {scope === "BOTH" ? (
                        <Box>
                          <Typography sx={{ fontSize: 12.5, fontWeight: 800, color: "primary.900" }}>
                            Compartidos ({preview.sharedItems?.length || 0}) · asignados a {SECTOR_LABELS[sharedItemsSector]}
                          </Typography>
                          {preview.sharedItems?.length ? (
                            <Box component="ul" aria-label="Elementos compartidos" sx={{ mt: 0.5, mb: 0, pl: 2.5, maxHeight: 92, overflowY: "auto" }}>
                              {preview.sharedItems.map((item) => (
                                <Typography component="li" key={`${item.itemType}-${item.itemId}`} sx={{ fontSize: 12, color: "neutral.700", py: 0.15 }}>
                                  {item.name}
                                </Typography>
                              ))}
                            </Box>
                          ) : (
                            <Typography sx={{ mt: 0.4, fontSize: 12, color: "neutral.700" }}>
                              No hay elementos compartidos en este conteo.
                            </Typography>
                          )}
                        </Box>
                      ) : null}
                    </>
                  ) : null}
                </Stack>
              </Box>

              <TextField
                type="date"
                label="Fecha de carga"
                value={deadlineDate}
                onChange={(event) => {
                  const nextDeadlineDate = event.target.value;
                  setDeadlineDate(nextDeadlineDate);
                  setFieldErrors((current) => ({
                    ...current,
                    deadlineDate:
                      nextDeadlineDate && nextDeadlineDate < minDate
                        ? "La fecha de carga no puede estar vencida."
                        : "",
                  }));
                  setSubmitError("");
                }}
                disabled={saving}
                error={Boolean(fieldErrors.deadlineDate)}
                helperText={fieldErrors.deadlineDate || null}
                InputLabelProps={{ shrink: true }}
                inputProps={{
                  min: minDate,
                  "aria-label": "Fecha de carga",
                }}
                FormHelperTextProps={{
                  role: fieldErrors.deadlineDate ? "alert" : undefined,
                  sx: {
                    mx: 0.4,
                    mt: 0.45,
                    fontSize: 11.5,
                    fontWeight: 600,
                  },
                }}
                sx={fieldSx}
              />

              {submitError ? (
                <Alert severity="error" sx={{ borderRadius: "14px", fontWeight: 650 }}>
                  {submitError}
                </Alert>
              ) : null}
            </Stack>
          </Box>
        </DialogContent>

        <DialogActions sx={{ px: 3, pb: 2.5, pt: 1.5, gap: 1, flexShrink: 0, bgcolor: "neutral.200" }}>
          <Button
            type="button"
            variant="contained"
            disabled={saving}
            onClick={handleClose}
            sx={{
              minWidth: 120,
              borderRadius: "20px",
              textTransform: "none",
              fontWeight: 800,
              bgcolor: "neutral.100",
              color: "primary.900",
              boxShadow: "none",
              "&:hover": {
                bgcolor: "neutral.150",
                boxShadow: "none",
              },
            }}
          >
            Cancelar
          </Button>
          <Button
            type="submit"
            variant="contained"
            disabled={!canSubmit}
            startIcon={saving ? <CircularProgress size={16} color="inherit" /> : null}
            sx={{
              minWidth: 120,
              borderRadius: "20px",
              textTransform: "none",
              fontWeight: 800,
              bgcolor: "primary.500",
              color: "common.white",
              boxShadow: "none",
              "&:hover": {
                bgcolor: "primary.600",
                boxShadow: "none",
              },
              "&.Mui-disabled": {
                bgcolor: "neutral.150",
                color: "neutral.500",
              },
            }}
          >
            {saving ? "Guardando..." : "Guardar"}
          </Button>
        </DialogActions>
      </Box>
    </Dialog>
  );
}
